import * as React from "react";
import { Link, Outlet, useOutlet } from "react-router";
import { useCurrentProject } from "@/lib/project";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Database, Zap, CheckCircle, BarChart2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/bespoke";
import { formatElapsed } from "@/lib/format";

export type EvalDataset = {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
};

export type EvalGrader = {
  id: string;
  projectId: string;
  name: string;
  type: "deterministic" | "rubric" | "human";
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type EvalRun = {
  id: string;
  projectId: string;
  datasetId: string;
  graderId: string;
  name: string;
  environment: string;
  trigger: string;
  status: "Running" | "Completed" | "Failed" | "Cancelled";
  totalItems: number;
  completedItems: number;
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

async function fetchOverview(projectId: string) {
  const [datasets, graders, runs] = await Promise.all([
    fetch(`/api/v1/projects/${projectId}/eval/datasets`, {
      method: "GET",
      credentials: "same-origin",
    })
      .then((r) => r.json())
      .then((d) => d.datasets),
    fetch(`/api/v1/projects/${projectId}/eval/graders`, {
      method: "GET",
      credentials: "same-origin",
    })
      .then((r) => r.json())
      .then((d) => d.graders),
    fetch(`/api/v1/projects/${projectId}/eval/runs`, { method: "GET", credentials: "same-origin" })
      .then((r) => r.json())
      .then((d) => d.runs),
  ]);
  return { datasets, graders, runs };
}

function StatCard({
  title,
  value,
  icon: Icon,
  color = "text-accent-text",
  href,
}: {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color?: string;
  href?: string;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <dt className="text-xs font-medium text-muted-foreground">{title}</dt>
            <dd className="mt-1 text-3xl font-semibold tabular-nums">{value}</dd>
          </div>
          <div className={`p-3 rounded-lg bg-muted ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        {href && (
          <div className="mt-4">
            <Link
              to={href}
              className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-accent-text hover:underline md:min-h-0"
            >
              View all
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentRunRow({ run }: { run: EvalRun }) {
  // The badge variants carry the status tokens, which meet contrast in both themes. The raw
  // palette classes these replaced were light-only and failed at 2.94:1 in light mode.
  const statusVariants: Record<EvalRun["status"], "info" | "success" | "destructive" | "muted"> = {
    Running: "info",
    Completed: "success",
    Failed: "destructive",
    Cancelled: "muted",
  };

  const progress = run.totalItems > 0 ? Math.round((run.completedItems / run.totalItems) * 100) : 0;

  return (
    <tr className="hover:bg-muted/50">
      <td className="p-3 font-medium">
        <Link to={`/eval/runs/${run.id}/results`} className="hover:underline">
          {run.name}
        </Link>
      </td>
      <td className="p-3 text-muted-foreground">{run.environment}</td>
      <td className="p-3">
        <Badge variant={statusVariants[run.status]}>{run.status}</Badge>
      </td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 max-w-[120px] h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-xs text-muted-foreground tabular-nums w-[45px] text-right">
            {run.completedItems}/{run.totalItems}
          </span>
        </div>
      </td>
      <td className="p-3 text-muted-foreground text-sm">{formatElapsed(run.startedAt)}</td>
      <td className="p-3">
        <Link
          to={`/eval/runs/${run.id}/results`}
          className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-accent-text hover:underline md:min-h-0"
        >
          View
          <ChevronRight className="h-3 w-3" />
        </Link>
      </td>
    </tr>
  );
}

export function EvalPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project } = useCurrentProject();
  const projectId = project?.id;
  // A matched child route (datasets, graders, runs, ...) renders through the Outlet.
  // The overview renders only when no child route matches, which is the /eval path itself.
  const child = useOutlet();

  const { data, isLoading } = useQuery({
    queryKey: ["eval-overview", projectId],
    queryFn: () => fetchOverview(projectId!),
    enabled: !!projectId && !child,
  });

  const recentRuns = data?.runs.slice(0, 5) ?? [];

  // A child route keeps the page heading: opening /eval/criteria directly by URL must still
  // have an h1, and returning only the outlet left those routes with none. The child renders
  // as h2 beneath this heading, which is the outline it already declares.
  if (child)
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Eval"
          headingLevel={headingLevel}
          description="Create datasets, define graders, and run evaluations."
        />
        <Outlet />
      </div>
    );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Eval" headingLevel={headingLevel} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6 animate-pulse">
                <div className="h-4 bg-muted rounded w-1/4" />
                <div className="mt-4 h-8 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Eval"
        headingLevel={headingLevel}
        description="Create datasets, define graders, and run evaluations."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/eval/datasets">
              <Button variant="outline">
                <Database className="mr-2 h-4 w-4" /> Datasets
              </Button>
            </Link>
            <Link to="/eval/graders">
              <Button variant="outline">
                <Zap className="mr-2 h-4 w-4" /> Graders
              </Button>
            </Link>
            <Link to="/eval/runs">
              <Button>
                <Play className="mr-2 h-4 w-4" /> New run
              </Button>
            </Link>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Datasets"
          value={data?.datasets.length ?? 0}
          icon={Database}
          color="text-info"
          href="/eval/datasets"
        />
        <StatCard
          title="Graders"
          value={data?.graders.length ?? 0}
          icon={Zap}
          color="text-info"
          href="/eval/graders"
        />
        <StatCard
          title="Total runs"
          value={data?.runs.length ?? 0}
          icon={BarChart2}
          color="text-success"
          href="/eval/runs"
        />
        <StatCard
          title="Completed runs"
          value={data?.runs.filter((r: EvalRun) => r.status === "Completed").length ?? 0}
          icon={CheckCircle}
          color="text-success"
          href="/eval/runs"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">
                    Run
                  </th>
                  <th className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">
                    Environment
                  </th>
                  <th className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">
                    Progress
                  </th>
                  <th className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">
                    Started
                  </th>
                  <th className="h-10 px-4 text-left text-xs font-medium text-muted-foreground"></th>
                </tr>
              </thead>
              <tbody>
                {recentRuns.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center">
                      <p className="font-medium">No eval runs yet</p>
                      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                        Create a dataset and a grader, then start your first eval run.
                      </p>
                      <div className="mt-4 flex justify-center gap-2">
                        <Link to="/eval/datasets">
                          <Button variant="outline" size="sm">
                            <Database className="mr-2 h-4 w-4" /> Create dataset
                          </Button>
                        </Link>
                        <Link to="/eval/graders">
                          <Button variant="outline" size="sm">
                            <Zap className="mr-2 h-4 w-4" /> Create grader
                          </Button>
                        </Link>
                        <Link to="/eval/runs">
                          <Button size="sm">
                            <Play className="mr-2 h-4 w-4" /> New run
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ) : (
                  recentRuns.map((run: EvalRun) => <RecentRunRow key={run.id} run={run} />)
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-3">
        <Link to="/eval/datasets">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6">
              <div className="p-3 rounded-lg bg-muted text-info mb-3">
                <Database className="h-6 w-6" />
              </div>
              <h3 className="font-semibold">Datasets</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Manage collections of input/output pairs for evaluation.
              </p>
              <p className="mt-2 text-sm font-medium text-accent-text flex items-center gap-1">
                {data?.datasets.length ?? 0} datasets
                <ChevronRight className="h-3 w-3" />
              </p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/eval/graders">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6">
              <div className="p-3 rounded-lg bg-muted text-info mb-3">
                <Zap className="h-6 w-6" />
              </div>
              <h3 className="font-semibold">Graders</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Define deterministic checks, rubrics, or human review criteria.
              </p>
              <p className="mt-2 text-sm font-medium text-accent-text flex items-center gap-1">
                {data?.graders.length ?? 0} graders
                <ChevronRight className="h-3 w-3" />
              </p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/eval/runs">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6">
              <div className="p-3 rounded-lg bg-muted text-success mb-3">
                <BarChart2 className="h-6 w-6" />
              </div>
              <h3 className="font-semibold">Runs & Results</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Execute graders against datasets and view scored results.
              </p>
              <p className="mt-2 text-sm font-medium text-accent-text flex items-center gap-1">
                {data?.runs.length ?? 0} runs
                <ChevronRight className="h-3 w-3" />
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
